/**
 * Builds the invoice list query string from a typed filter object.
 *
 * The old app built this string by hand in `FilterModal.generateQueryString()`,
 * stashed the raw string in `localStorage.filterObj`, and then read that string
 * back *during render* inside the query argument. Values were never encoded, so
 * a supplier name with an `&` in it silently corrupted the request. Everything
 * goes through URLSearchParams now, and the source of truth is a plain object.
 */

const isSet = (value) => value !== undefined && value !== null && value !== "";

function appendEach(params, key, values) {
  (values ?? []).forEach((value) => {
    if (isSet(value)) params.append(key, value);
  });
}

/**
 * @param {object} options
 * @param {number} options.page
 * @param {number} options.pageSize
 * @param {string} [options.query]        free-text search
 * @param {string} [options.sortBy]
 * @param {string} [options.sortDirection]
 * @param {string|number} [options.categoryId]
 * @param {string[]} [options.failureTypes]
 * @param {object} [options.filters]      the advanced filter panel state
 */
export function buildInvoiceQuery({
  page,
  pageSize,
  query,
  sortBy,
  sortDirection,
  categoryId,
  failureTypes,
  filters,
} = {}) {
  const params = new URLSearchParams();

  if (isSet(query)) params.append("Query", query);
  if (isSet(page)) params.append("Page", page);
  if (isSet(pageSize)) params.append("PageSize", pageSize);
  if (isSet(sortBy)) params.append("SortBy", sortBy);
  if (isSet(sortDirection)) params.append("SortDirection", sortDirection);
  if (isSet(categoryId)) params.append("CategoryId", categoryId);

  appendEach(params, "FailureType", failureTypes);

  if (filters) {
    appendEach(params, "ReviewStatusId", filters.reviewStatusIds);
    appendEach(params, "ProcessingStatusId", filters.processingStatusIds);

    if (isSet(filters.createdOnAfter)) params.append("CreatedOnGreaterThan", filters.createdOnAfter);
    if (isSet(filters.createdOnBefore)) params.append("CreatedOnLessThan", filters.createdOnBefore);
    if (isSet(filters.invoiceDateAfter)) {
      params.append("InvoiceDateGreaterThan", filters.invoiceDateAfter);
    }
    if (isSet(filters.invoiceDateBefore)) {
      params.append("InvoiceDateLessThan", filters.invoiceDateBefore);
    }
    if (isSet(filters.totalMin)) params.append("TotalGreaterThan", filters.totalMin);
    if (isSet(filters.totalMax)) params.append("TotalLessThan", filters.totalMax);
    if (isSet(filters.assignedToId)) params.append("AssignedToId", filters.assignedToId);
  }

  return params.toString();
}

/**
 * `/invoices/to-approve` accepts the same filter vocabulary as the invoice list,
 * so every parameter it documents is emitted here — review/processing status,
 * review type and failure type as repeated keys, the two date ranges, the amount
 * range, and the three user ids.
 */
export function buildApprovalRequestQuery({
  page,
  pageSize,
  query,
  sortBy,
  sortDirection,
  filters,
} = {}) {
  const params = new URLSearchParams();

  if (isSet(page)) params.append("page", page);
  if (isSet(pageSize)) params.append("pageSize", pageSize);
  if (isSet(query)) params.append("Query", query);
  if (isSet(sortBy)) params.append("SortBy", sortBy);
  if (isSet(sortDirection)) params.append("SortDirection", sortDirection);

  if (filters) {
    appendEach(params, "ReviewStatusId", filters.reviewStatusIds);
    appendEach(params, "ProcessingStatusId", filters.processingStatusIds);
    appendEach(params, "ReviewTypeId", filters.reviewTypeIds);
    appendEach(params, "FailureType", filters.failureTypes);

    if (isSet(filters.createdOnAfter)) params.append("CreatedOnGreaterThan", filters.createdOnAfter);
    if (isSet(filters.createdOnBefore)) params.append("CreatedOnLessThan", filters.createdOnBefore);
    if (isSet(filters.updatedOnAfter)) params.append("UpdatedOnGreaterThan", filters.updatedOnAfter);
    if (isSet(filters.updatedOnBefore)) params.append("UpdatedOnLessThan", filters.updatedOnBefore);

    if (isSet(filters.totalMin)) params.append("TotalGreaterThan", filters.totalMin);
    if (isSet(filters.totalMax)) params.append("TotalLessThan", filters.totalMax);

    if (isSet(filters.assignedToId)) params.append("AssignedToId", filters.assignedToId);
    if (isSet(filters.createdById)) params.append("CreatedById", filters.createdById);
    if (isSet(filters.updatedById)) params.append("UpdatedById", filters.updatedById);
  }

  return params.toString();
}

export function buildCreationLogQuery({
  page,
  pageSize,
  query,
  createdById,
  createdOnAfter,
  createdOnBefore,
  status,
  invoiceGuid,
} = {}) {
  const params = new URLSearchParams();

  if (isSet(page)) params.append("page", page);
  if (isSet(pageSize)) params.append("pageSize", pageSize);
  if (isSet(query)) params.append("query", query);
  if (isSet(createdById)) params.append("createdById", createdById);
  if (isSet(createdOnAfter)) params.append("createdOnAfter", createdOnAfter);
  if (isSet(createdOnBefore)) params.append("createdOnBefore", createdOnBefore);
  if (isSet(status)) params.append("status", status);
  if (isSet(invoiceGuid)) params.append("invoiceGuid", invoiceGuid);

  return params.toString();
}
